import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, RefreshCw, Check, X, ImageIcon, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const T = {
  navy: "#002868",
  red: "#B22234",
  gold: "#C5A55A",
  green: "#16a34a",
  border: "rgba(27,27,27,0.12)",
};
const sans = "'Instrument Sans', system-ui, sans-serif";

interface Props {
  postId: string;
  title: string;
  excerpt?: string;
  contentType?: string;
  currentOgImageUrl?: string;
  onImageSelected: (url: string) => void;
}

interface OGImageOption {
  id: string;
  blog_post_id: string;
  image_data: string;
  prompt_used: string | null;
  generation_run: number;
  status: "pending" | "selected" | "rejected";
  feedback: string | null;
  created_at: string;
}

export function OGImageSelector({
  postId,
  title,
  excerpt,
  contentType,
  currentOgImageUrl,
  onImageSelected,
}: Props) {
  const queryClient = useQueryClient();
  const [showOptions, setShowOptions] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [showFeedbackBox, setShowFeedbackBox] = useState(false);

  // Load existing options for this post
  const { data: options = [], isLoading: loadingOptions } = useQuery<OGImageOption[]>({
    queryKey: ["blog_og_options", postId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_og_image_options" as any)
        .select("*")
        .eq("blog_post_id", postId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data || []) as unknown as OGImageOption[];
    },
    enabled: !!postId,
  });

  // Latest generation run options (pending/recent)
  const latestRun = options.length > 0 ? Math.max(...options.map(o => o.generation_run)) : 0;
  const latestOptions = options.filter(o => o.generation_run === latestRun && o.status !== "rejected");
  const selectedOption = options.find(o => o.status === "selected");

  // Generate 5 new options via CF Function
  const generateMutation = useMutation({
    mutationFn: async (feedbackText?: string) => {
      const { data: sess } = await supabase.auth.getSession();
      const res = await fetch("/api/admin/generate-og-images", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Endpoint verifies admin role server-side from this JWT
          ...(sess?.session?.access_token ? { Authorization: `Bearer ${sess.session.access_token}` } : {}),
        },
        body: JSON.stringify({
          postId,
          title,
          excerpt,
          contentType,
          feedback: feedbackText || undefined,
          generationRun: latestRun + 1,
        }),
      });

      const json = await res.json() as { images?: string[]; prompt?: string; error?: string };

      if (!res.ok || json.error) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }

      // Store the 5 images in Supabase
      const rows = (json.images || []).map((imageData: string) => ({
        blog_post_id: postId,
        image_data: imageData,
        prompt_used: json.prompt,
        generation_run: latestRun + 1,
        status: "pending",
        feedback: feedbackText || null,
      }));

      const { error } = await supabase
        .from("blog_og_image_options" as any)
        .insert(rows as any);
      if (error) throw error;

      return rows.length;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog_og_options", postId] });
      setFeedback("");
      setShowFeedbackBox(false);
      setShowOptions(true);
    },
  });

  // Select an image option
  const selectMutation = useMutation({
    mutationFn: async (option: OGImageOption) => {
      // Mark this option as selected
      await supabase
        .from("blog_og_image_options" as any)
        .update({ status: "selected" } as any)
        .eq("id", option.id);

      // Un-select any previously selected option
      await supabase
        .from("blog_og_image_options" as any)
        .update({ status: "pending" } as any)
        .eq("blog_post_id", postId)
        .neq("id", option.id)
        .eq("status", "selected");

      // Update the blog post og_image_url with the base64 data URL
      await supabase
        .from("blog_posts" as any)
        .update({ og_image_url: option.image_data, updated_at: new Date().toISOString() } as any)
        .eq("id", postId);

      onImageSelected(option.image_data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog_og_options", postId] });
      queryClient.invalidateQueries({ queryKey: ["admin_all_blog_posts"] });
    },
  });

  // Reject all and regenerate with feedback
  const handleRegenerate = () => {
    if (feedback.trim()) {
      generateMutation.mutate(feedback.trim());
    }
  };

  const isGenerating = generateMutation.isPending;

  return (
    <div style={{ fontFamily: sans }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ImageIcon size={15} color={T.navy} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(27,27,27,0.62)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            OG Image
          </span>
          {selectedOption && (
            <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, backgroundColor: "rgba(22,163,74,0.1)", color: T.green, fontWeight: 600 }}>
              Selected
            </span>
          )}
          {currentOgImageUrl && !selectedOption && (
            <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, backgroundColor: "rgba(0,40,104,0.08)", color: T.navy, fontWeight: 500 }}>
              Custom URL set
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {options.length > 0 && (
            <button
              onClick={() => setShowOptions(v => !v)}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", border: `1px solid ${T.border}`, borderRadius: 6, background: "none", cursor: "pointer", fontSize: 12, color: "#555" }}
            >
              {showOptions ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {latestOptions.length} option{latestOptions.length !== 1 ? "s" : ""}
            </button>
          )}
          <Button
            size="sm"
            onClick={() => generateMutation.mutate(undefined)}
            disabled={isGenerating}
            style={{ backgroundColor: T.navy, color: "white", fontSize: 12, gap: 6, display: "flex", alignItems: "center" }}
          >
            {isGenerating ? (
              <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Generating…</>
            ) : (
              <><Sparkles size={13} /> Generate 5 Options</>
            )}
          </Button>
        </div>
      </div>

      {/* Error */}
      {generateMutation.isError && (
        <div style={{ padding: "8px 12px", backgroundColor: "rgba(178,34,52,0.08)", borderRadius: 6, color: T.red, fontSize: 12, marginBottom: 8 }}>
          {generateMutation.error instanceof Error ? generateMutation.error.message : "Generation failed"}
          {String(generateMutation.error).includes("GEMINI_API_KEY") && (
            <span> — Add <code style={{ fontSize: 11 }}>GEMINI_API_KEY</code> as a CF Pages secret.</span>
          )}
        </div>
      )}

      {/* Currently selected / current URL preview */}
      {(selectedOption || currentOgImageUrl) && !showOptions && (
        <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", border: `2px solid ${T.green}`, marginBottom: 8 }}>
          <img
            src={selectedOption?.image_data || currentOgImageUrl}
            alt="Current OG image"
            style={{ width: "100%", height: "auto", display: "block", maxHeight: 200, objectFit: "cover" }}
          />
          <div style={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 4 }}>
            <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, backgroundColor: T.green, color: "white", fontWeight: 600 }}>
              Active OG Image
            </span>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loadingOptions && (
        <div style={{ height: 80, borderRadius: 6, backgroundColor: "rgba(0,0,0,0.04)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#999" }}>
          Loading options…
        </div>
      )}

      {/* Image options grid */}
      {showOptions && latestOptions.length > 0 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 12 }}>
            {latestOptions.map((opt) => {
              const isSelected = opt.status === "selected" || (selectedOption?.id === opt.id);
              return (
                <div
                  key={opt.id}
                  onClick={() => !selectMutation.isPending && selectMutation.mutate(opt)}
                  style={{
                    position: "relative",
                    borderRadius: 6,
                    overflow: "hidden",
                    border: `2px solid ${isSelected ? T.green : T.border}`,
                    cursor: "pointer",
                    transition: "border-color 0.15s, transform 0.1s",
                    transform: "scale(1)",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = isSelected ? T.green : T.navy)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = isSelected ? T.green : T.border)}
                >
                  <img
                    src={opt.image_data}
                    alt={`Option ${latestOptions.indexOf(opt) + 1}`}
                    style={{ width: "100%", height: "auto", display: "block", aspectRatio: "16/9", objectFit: "cover" }}
                  />
                  {isSelected && (
                    <div style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", backgroundColor: T.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Check size={12} color="white" />
                    </div>
                  )}
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    padding: "4px 6px",
                    background: "linear-gradient(transparent, rgba(0,0,0,0.5))",
                    fontSize: 10, color: "rgba(255,255,255,0.8)", textAlign: "center",
                  }}>
                    Option {latestOptions.indexOf(opt) + 1}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Decline + feedback */}
          {!showFeedbackBox ? (
            <button
              onClick={() => setShowFeedbackBox(true)}
              style={{ fontSize: 12, color: "#888", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
            >
              None of these — give feedback for next generation
            </button>
          ) : (
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "rgba(27,27,27,0.62)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>
                Feedback for next generation
              </label>
              <textarea
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="e.g. 'Make it more patriotic, add dollar signs, less abstract, warmer colors…'"
                rows={2}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, fontFamily: sans, boxSizing: "border-box", resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
                <Button
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={!feedback.trim() || isGenerating}
                  style={{ backgroundColor: T.navy, color: "white", fontSize: 12 }}
                >
                  {isGenerating ? "Regenerating…" : "Regenerate with Feedback"}
                </Button>
                <button
                  onClick={() => { setShowFeedbackBox(false); setFeedback(""); }}
                  style={{ fontSize: 12, color: "#888", background: "none", border: "none", cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Generation run history */}
      {latestRun > 1 && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
          Generation run {latestRun} of this post's images
        </div>
      )}
    </div>
  );
}
